import { Pressable } from 'react-native';
import { Text, useTheme, XStack } from 'tamagui';
// Lucide replaces the hand-rolled inline chevron (identical marks; the
// old 2.5 stroke and 18px size move to the call sites).
import { ChevronDown, ChevronUp } from 'lucide-react-native';

function Chevron({ direction, color }: { direction: 'up' | 'down'; color: string }) {
  const Icon = direction === 'up' ? ChevronUp : ChevronDown;

  return <Icon size={20} strokeWidth={1.75} color={color} />;
}

export type VoteDirection = 'up' | 'down';

// react-native-svg's stroke prop needs a RESOLVED color -- Tamagui token
// strings like '$primary' are meaningless to it and render invisibly
// (the same gotcha glass-surface.tsx's useGlassAccentColor exists for).
// useTheme() hands back real resolved values per light/dark.
function useVoteColors() {
  const theme = useTheme();

  return {
    active: String(theme.primary?.val ?? '#97271c'),
    inactive: String(theme.colorTertiary?.val ?? '#8a8175'),
  };
}

export function VoteControl({
  score,
  viewerVote,
  onVote,
  onRemove,
}: {
  score: number;
  viewerVote: VoteDirection | undefined;
  onVote: (direction: VoteDirection) => void;
  onRemove: () => void;
}) {
  // Reddit toggle semantics: tapping the active direction removes the
  // vote; tapping the other one switches to it (PUT replaces, DELETE
  // removes -- one vote per user per target).
  const colors = useVoteColors();

  const press = (direction: VoteDirection) => {
    if (viewerVote === direction) onRemove();
    else onVote(direction);
  };

  return (
    <XStack alignItems="center" gap="$2">
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Upvote"
        accessibilityState={{ selected: viewerVote === 'up' }}
        aria-pressed={viewerVote === 'up'}
        style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
        onPress={(event) => {
          // Never let a vote tap bubble to an enclosing card pressable
          // -- a near-miss on the (once-invisible) chevron navigated the
          // whole screen instead of voting.
          event.stopPropagation();
          press('up');
        }}
      >
        <Chevron direction="up" color={viewerVote === 'up' ? colors.active : colors.inactive} />
      </Pressable>

      {/* Decision 7 (issue #561): the count only when non-zero -- a zero
          renders nothing between the chevrons (previously an empty
          20px-wide spacer sat there). The chevrons' own gap keeps them
          apart either way. */}
      {score !== 0 && (
        <Text
          color="$colorSecondary"
          fontSize="$meta"
          lineHeight="$meta"
          fontWeight="$semibold"
          minWidth={20}
          textAlign="center"
        >
          {score}
        </Text>
      )}

      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Downvote"
        accessibilityState={{ selected: viewerVote === 'down' }}
        aria-pressed={viewerVote === 'down'}
        style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
        onPress={(event) => {
          event.stopPropagation();
          press('down');
        }}
      >
        <Chevron direction="down" color={viewerVote === 'down' ? colors.active : colors.inactive} />
      </Pressable>
    </XStack>
  );
}
